import { Box, Card, CardHeader, Typography, LinearProgress } from '@mui/material';

import { CircularProgress, Divider } from '@occc-app/design-system';
import { pluralize } from '@occc-app/lib/strings';
import { endOfDateTime, diffDateTime, formatDateTime, DateTimeFormat } from '@occc-app/lib/date';
import {
  Retailer_Network_Performance_Period,
  RetailerNetworkPerformanceFilter,
} from '@occc/graphql';

import { RetailerNetworkMonthlyGoals } from './retailer-network-monthly-goals.component';
import { getNarrativeTextData } from './retailer-network-monthly-goals.utils';

import { useGetRetailerNetworkPerformanceQuery } from '../../../data-access/queries';
import { RetailerNetworkErrorMessage } from '../retailer-network-error-message.component';
import { RetailerNetworkNoData } from '../retailer-network-no-data.component';

type RetailerNetworkPerformanceContainerProps = {
  filter: Omit<RetailerNetworkPerformanceFilter, 'periodEquals'>;
};

const RetailerNetworkPerformanceContainer = ({
  filter,
}: RetailerNetworkPerformanceContainerProps) => {
  const { data, loading, error } = useGetRetailerNetworkPerformanceQuery({
    variables: {
      filter: {
        ...filter,
        periodEquals: Retailer_Network_Performance_Period.ThisMonth,
      },
    },
  });
  const retailerNetworkPerformance = data?.retailerNetworkPerformance;

  const dateNow = new Date();
  const endMonth = endOfDateTime(dateNow, 'month');
  const daysLeft = diffDateTime(endMonth, dateNow, 'day');

  const renderData = () => {
    if (loading && !retailerNetworkPerformance) {
      return (
        <Box p={3} display='flex' justifyContent='center' alignItems='center'>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return <RetailerNetworkErrorMessage />;
    }

    if (!loading && !retailerNetworkPerformance) {
      return <RetailerNetworkNoData />;
    }

    if (!retailerNetworkPerformance) {
      return null;
    }

    const newMembersGoalProgress = retailerNetworkPerformance.newMembers.goalProgress;
    const loyaltyTransactionsGoalProgress =
      retailerNetworkPerformance.loyaltyTransactions.goalProgress;

    return (
      <RetailerNetworkMonthlyGoals
        narrativeTextData={getNarrativeTextData(
          newMembersGoalProgress,
          loyaltyTransactionsGoalProgress,
          daysLeft,
        )}
        newMembersGoal={newMembersGoalProgress.goal}
        newMembersGoalProgress={newMembersGoalProgress.progress}
        loyaltyTransactionsGoal={loyaltyTransactionsGoalProgress.goal}
        loyaltyTransactionsGoalProgress={loyaltyTransactionsGoalProgress.progress}
        homeStoreMembers={retailerNetworkPerformance.homeStoreMembers}
      />
    );
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        sx={{ p: 3 }}
        title={
          <Box display='flex' flexDirection='row' alignItems='center'>
            <Typography variant='h6'>
              {`Your ${formatDateTime(dateNow, DateTimeFormat.Month)} Goals`}
            </Typography>
            <Typography marginLeft={1} color='textSecondary' variant='body2'>
              ({daysLeft} {pluralize(daysLeft, 'day')} left)
            </Typography>
          </Box>
        }
      />
      <Divider />
      {loading && retailerNetworkPerformance && <LinearProgress />}
      {renderData()}
    </Card>
  );
};

export default RetailerNetworkPerformanceContainer;
